import React, { useState } from 'react';
import { Box, IconButton, ToggleButtonGroup, ToggleButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, ViewComfy as NormalViewIcon, Layers as MasterViewIcon, Article as SlideOnlyIcon, } from '@mui/icons-material';
import { SlideView } from './SlideView.js';
export const PPTXViewer = ({ pptxData, initialSlide = 0, initialViewMode = 'normal', showFilmstrip = true, filmstripPosition = 'right', width = 960, height = 540, showModeToggle = true, showSpeakerNotes = false, speakerNotesHeight = 150, onSlideChange, onViewModeChange, }) => {
    const [currentSlide, setCurrentSlide] = useState(initialSlide);
    const [viewMode, setViewMode] = useState(initialViewMode);
    const handleSlideChange = (newSlide) => {
        if (newSlide >= 0 && newSlide < pptxData.slides.length) {
            setCurrentSlide(newSlide);
            if (onSlideChange) {
                onSlideChange(newSlide);
            }
        }
    };
    const handleViewModeChange = (_event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
            if (onViewModeChange) {
                onViewModeChange(newMode);
            }
        }
    };
    const getRenderMode = () => {
        switch (viewMode) {
            case 'master':
                return 'master-layout';
            case 'slide':
                return 'complete';
            case 'normal':
            default:
                return 'complete';
        }
    };
    const thumbWidth = filmstripPosition === 'bottom' ? 200 : 150;
    const thumbHeight = Math.round(thumbWidth * (pptxData.size.height / pptxData.size.width));
    const showFilmstripInView = showFilmstrip && viewMode === 'normal';
    return (React.createElement(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: 2,
        } },
        showModeToggle && (React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', p: 1 } },
            React.createElement(ToggleButtonGroup, { value: viewMode, exclusive: true, onChange: handleViewModeChange, "aria-label": "view mode", size: "small" },
                React.createElement(ToggleButton, { value: "normal", "aria-label": "normal view" },
                    React.createElement(NormalViewIcon, { sx: { mr: 1 } }),
                    "Normal View"),
                React.createElement(ToggleButton, { value: "master", "aria-label": "master view" },
                    React.createElement(MasterViewIcon, { sx: { mr: 1 } }),
                    "Master View"),
                React.createElement(ToggleButton, { value: "slide", "aria-label": "slide view" },
                    React.createElement(SlideOnlyIcon, { sx: { mr: 1 } }),
                    "Slide View")))),
        React.createElement(Box, { sx: {
                flex: 1,
                display: 'flex',
                flexDirection: filmstripPosition === 'bottom' ? 'column' : 'row',
                gap: 2,
                minHeight: 0,
            } },
            showFilmstripInView && filmstripPosition === 'left' && (React.createElement(Box, { sx: {
                    width: thumbWidth + 48,
                    minHeight: 0,
                    flexShrink: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1,
                } }, pptxData.slides.map((slide, index) => (React.createElement(Box, { key: index, onClick: () => handleSlideChange(index), sx: {
                    cursor: 'pointer',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                } },
                React.createElement(Box, { sx: {
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        color: 'text.secondary',
                    } }, index + 1),
                React.createElement(Box, { sx: {
                        width: thumbWidth,
                        height: thumbHeight,
                        border: currentSlide === index ? '3px solid #1976d2' : '1px solid #ccc',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '&:hover': {
                            borderColor: '#1976d2',
                        },
                    } },
                    React.createElement(SlideView, { slide: slide, slideWidth: pptxData.size.width, slideHeight: pptxData.size.height, width: thumbWidth, height: thumbHeight, renderMode: "complete" }))))))),
            React.createElement(Box, { sx: {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                } },
                React.createElement(Box, { sx: { position: 'relative', display: 'inline-block' } },
                    React.createElement(Box, { sx: {
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            overflow: 'hidden',
                            boxShadow: 2,
                        } },
                        React.createElement(SlideView, { slide: pptxData.slides[currentSlide], slideWidth: pptxData.size.width, slideHeight: pptxData.size.height, width: width, height: height, renderMode: getRenderMode() }))),
                React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 } },
                    React.createElement(IconButton, { onClick: () => handleSlideChange(currentSlide - 1), disabled: currentSlide === 0, size: "small", sx: {
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': {
                                bgcolor: 'background.paper',
                            },
                        } },
                        React.createElement(ChevronLeft, null)),
                    React.createElement(Typography, { variant: "body2", color: "text.secondary" },
                        "Slide ",
                        currentSlide + 1,
                        " of ",
                        pptxData.slides.length),
                    React.createElement(IconButton, { onClick: () => handleSlideChange(currentSlide + 1), disabled: currentSlide === pptxData.slides.length - 1, size: "small", sx: {
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': {
                                bgcolor: 'background.paper',
                            },
                        } },
                        React.createElement(ChevronRight, null)))),
            showFilmstripInView && filmstripPosition === 'right' && (React.createElement(Box, { sx: {
                    width: thumbWidth + 48,
                    minHeight: 0,
                    flexShrink: 0,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1,
                } }, pptxData.slides.map((slide, index) => (React.createElement(Box, { key: index, onClick: () => handleSlideChange(index), sx: {
                    cursor: 'pointer',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                } },
                React.createElement(Box, { sx: {
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        color: 'text.secondary',
                    } }, index + 1),
                React.createElement(Box, { sx: {
                        width: thumbWidth,
                        height: thumbHeight,
                        border: currentSlide === index ? '3px solid #1976d2' : '1px solid #ccc',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '&:hover': {
                            borderColor: '#1976d2',
                        },
                    } },
                    React.createElement(SlideView, { slide: slide, slideWidth: pptxData.size.width, slideHeight: pptxData.size.height, width: thumbWidth, height: thumbHeight, renderMode: "complete" }))))))),
            showFilmstripInView && filmstripPosition === 'bottom' && (React.createElement(Box, { sx: {
                    width: '100%',
                    height: thumbHeight + 48,
                    minWidth: 0,
                    flexShrink: 0,
                    overflowX: 'auto',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    p: 1,
                } }, pptxData.slides.map((slide, index) => (React.createElement(Box, { key: index, onClick: () => handleSlideChange(index), sx: {
                    cursor: 'pointer',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                } },
                React.createElement(Box, { sx: {
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        color: 'text.secondary',
                    } }, index + 1),
                React.createElement(Box, { sx: {
                        width: thumbWidth,
                        height: thumbHeight,
                        border: currentSlide === index ? '3px solid #1976d2' : '1px solid #ccc',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '&:hover': {
                            borderColor: '#1976d2',
                        },
                    } },
                    React.createElement(SlideView, { slide: slide, slideWidth: pptxData.size.width, slideHeight: pptxData.size.height, width: thumbWidth, height: thumbHeight, renderMode: "complete" })))))))),
        showSpeakerNotes && (React.createElement(Box, { sx: {
                width: '100%',
                height: speakerNotesHeight,
                flexShrink: 0,
                border: '1px solid #ccc',
                borderRadius: 1,
                bgcolor: 'background.paper',
                p: 2,
                overflowY: 'auto',
            } },
            React.createElement(Typography, { variant: "caption", color: "text.secondary", fontWeight: "bold", sx: { display: 'block', mb: 1 } }, "Speaker Notes"),
            React.createElement(Typography, { variant: "body2", color: "text.primary", sx: { whiteSpace: 'pre-wrap' } }, pptxData.slides[currentSlide].notes || '(No notes for this slide)')))));
};
export default PPTXViewer;
//# sourceMappingURL=PPTXViewer.js.map